/**
 * Tutorial Progress Tracking
 *
 * Stores user's tutorial progress in localStorage for persistent tracking
 * across sessions. Tracks completed sections and last visited section.
 */

const STORAGE_KEY = 'lithejs-tutorial-progress';

export interface TutorialProgress {
  completedSections: string[];
  lastVisitedSection: string | null;
  lastVisitedAt: string | null;
  startedAt: string | null;
}

const defaultProgress: TutorialProgress = {
  completedSections: [],
  lastVisitedSection: null,
  lastVisitedAt: null,
  startedAt: null,
};

/**
 * Get the current tutorial progress from localStorage
 */
export function getProgress(): TutorialProgress {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return { ...defaultProgress };

    const parsed = JSON.parse(stored);
    return {
      ...defaultProgress,
      ...parsed,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to load tutorial progress:', error);
    return { ...defaultProgress };
  }
}

/**
 * Save tutorial progress to localStorage
 */
function saveProgress(progress: TutorialProgress): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to save tutorial progress:', error);
  }
}

/**
 * Mark a section as completed
 */
export function markSectionCompleted(sectionId: string): void {
  const progress = getProgress();

  if (!progress.completedSections.includes(sectionId)) {
    progress.completedSections.push(sectionId);
  }

  saveProgress(progress);
}

/**
 * Mark a section as not completed (uncomplete)
 */
export function markSectionIncomplete(sectionId: string): void {
  const progress = getProgress();

  progress.completedSections = progress.completedSections.filter(
    (id) => id !== sectionId
  );

  saveProgress(progress);
}

/**
 * Toggle section completion status
 */
export function toggleSectionCompletion(sectionId: string): boolean {
  const progress = getProgress();
  const isCompleted = progress.completedSections.includes(sectionId);

  if (isCompleted) {
    markSectionIncomplete(sectionId);
    return false;
  } else {
    markSectionCompleted(sectionId);
    return true;
  }
}

/**
 * Check if a section is completed
 */
export function isSectionCompleted(sectionId: string): boolean {
  const progress = getProgress();
  return progress.completedSections.includes(sectionId);
}

/**
 * Update the last visited section
 */
export function updateLastVisited(sectionId: string): void {
  const progress = getProgress();

  progress.lastVisitedSection = sectionId;
  progress.lastVisitedAt = new Date().toISOString();

  // Set startedAt if this is the first visit
  if (!progress.startedAt) {
    progress.startedAt = new Date().toISOString();
  }

  saveProgress(progress);
}

/**
 * Get the last visited section ID
 */
export function getLastVisitedSection(): string | null {
  return getProgress().lastVisitedSection;
}

/**
 * Calculate overall progress percentage
 */
export function getProgressPercentage(totalSections: number): number {
  if (totalSections === 0) return 0;

  const progress = getProgress();
  return Math.round((progress.completedSections.length / totalSections) * 100);
}

/**
 * Get the count of completed sections
 */
export function getCompletedCount(): number {
  return getProgress().completedSections.length;
}

/**
 * Reset all tutorial progress
 */
export function resetProgress(): void {
  saveProgress({ ...defaultProgress });
}

/**
 * Check if tutorial has been started
 */
export function hasTutorialStarted(): boolean {
  return getProgress().startedAt !== null;
}

/**
 * Get all completed section IDs
 */
export function getCompletedSections(): string[] {
  return getProgress().completedSections;
}
